export interface OccupancyPrice {
  occupancy: number;
  isEnabled: boolean;
  isBaseOccupancy: boolean;
}

const toOccupancy = (value: unknown): number | null => {
  const occupancy = Number(value);

  return Number.isInteger(occupancy) && occupancy > 0 ? occupancy : null;
};

/**
 * Normalize room type iframe prices into a list keyed by occupancy.
 *
 * Prices are stored as an object keyed by occupancy ({"1": {...}, "2": {...}});
 * older settings have no `index` field, so the key is the source of truth
 * and `index` is only used when present.
 */
export function getOccupancyPrices(prices: unknown): OccupancyPrice[] {
  if (!prices || typeof prices !== "object") {
    return [];
  }

  const entries: [string, any][] = Array.isArray(prices)
    ? prices.map((price, position) => [String(position + 1), price])
    : Object.entries(prices);

  return entries
    .map(([key, price]) => {
      if (!price || typeof price !== "object") {
        return null;
      }

      const occupancy = toOccupancy(price.index) ?? toOccupancy(key);

      return occupancy === null
        ? null
        : {
            occupancy,
            isEnabled: price.isEnabled === true,
            isBaseOccupancy: price.isBaseOccupancy === true,
          };
    })
    .filter((price): price is OccupancyPrice => price !== null)
    .sort((a, b) => a.occupancy - b.occupancy);
}

/**
 * Occupancies offered as availability rows: enabled ones plus the base occupancy.
 */
export function getVisibleOccupancies(prices: unknown): number[] {
  return getOccupancyPrices(prices)
    .filter((price) => price.isEnabled || price.isBaseOccupancy)
    .map((price) => price.occupancy);
}

/**
 * Stay price of a room type for the given occupancy. The API sends it per
 * occupancy in `occupancy_prices`; `price` is the price for the searched guests.
 */
export function getOccupancyRoomPrice(
  roomType: any,
  occupancy: number
): number {
  const price = roomType?.occupancy_prices?.[occupancy] ?? roomType?.price;
  const amount = Number(price);

  return Number.isFinite(amount) ? amount : 0;
}
